import { StyledTableLink } from '@common/Dependency/styled';
import { CodeGenerationContextProvider } from '@common/git/CodeGeneration/context';
import { ProjectContextProvider } from '@common/git/Project/context';
import { useSCIMUsersAndTeamsList } from '@common/SCIM/hooks';
import { TableActionDropdown } from '@common/TableActionDropdown';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { renderColumnWithEllipsis, renderTimeColumnWithEllipsis } from '@common/utils/renderUtils';
import { useIsVerticalApp } from '@common/VerticalApps/utils';
import { ProfessionalEditionPlan } from '@prophecy/graphql-types/enums';
import { ArrowNarrowRightIcon, DotsVerticalIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, ColumnType, Dropdown, SortOrder, Table, TableActions, Text, Tooltip } from '@prophecy/ui-v3';
import { FilterType } from '@prophecy/ui-v3/Table/types';
import { useEditionConfig } from 'frontend/core/src/context/edition';
import { useUser } from 'frontend/core/src/context/user';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DeleteConfirmationDialog } from '../DeleteEntity';
import { TeamWithAspects } from './api';
import { useDeleteTeam } from './DeleteTeam';

const useColumns = () => {
  const navigate = useNavigate();
  const [deleteTeamEntity, setDeleteTeamEntity] = useState<TeamWithAspects>();
  const { deleteTeam } = useDeleteTeam();
  const { globalTeamName } = useEditionConfig();
  const isVerticalApp = useIsVerticalApp();
  const currentUserID = useUser()?.user?.id;
  const { scimTeamsData, isTeamsLoading } = useSCIMUsersAndTeamsList(true, false);
  const columns = [
    {
      title: 'Name',
      fieldPath: 'name',
      dataIndex: 'name',
      key: 'name',
      filter: {
        type: 'search',
        searchInputProps: {
          placeholder: 'Search Team',
          'data-test-id': 'metadata-team-search-input'
        }
      },
      sortable: true,
      render: (name: string, rowData: TeamWithAspects) => {
        return (
          <StyledTableLink
            to={Private_Routes.Team.home.getUrl({ uid: rowData._id })}
            data-test-id={`team-config-team-link-${rowData._id}-link`}
            tooltip={rowData.InfoAspect?.description}>
            {renderColumnWithEllipsis(name)}
          </StyledTableLink>
        );
      }
    },
    isVerticalApp && {
      title: 'Plan',
      dataIndex: 'subscriptionPlan',
      key: 'subscriptionPlan',
      width: '120px',
      countPath: 'members',
      sortable: true,
      render: renderColumnWithEllipsis,
      filter: {
        type: FilterType.list,
        options: Object.values(ProfessionalEditionPlan).map((value) => ({ label: value, value }))
      }
    },
    {
      title: 'Members',
      dataIndex: 'member',
      key: 'member',
      width: '15%',
      countPath: 'members',
      sortable: true,
      computedValue: (rowData: TeamWithAspects) => {
        return rowData.members?.length || 0;
      },
      render: renderColumnWithEllipsis
    },
    {
      title: 'Created by',
      dataIndex: 'createdByFullName',
      key: 'createdByFullName',
      width: '20%',
      fieldPath: 'createdByFullName',
      sortable: true,
      render: renderColumnWithEllipsis
    },
    {
      title: 'Created at',
      width: '15%',
      dataIndex: 'created',
      key: 'created',
      fieldPath: 'created',
      sortable: true,
      render: renderTimeColumnWithEllipsis
    },
    {
      dataIndex: 'actions',
      key: 'actions',
      width: '180px',
      align: 'center',
      resizable: false,
      render: (v: string, rowData: TeamWithAspects) => {
        const isPersonal = rowData.teamKind === 'ProphecyManagedPersonal';
        const isGlobal = globalTeamName === rowData.name;
        const isAdmin = rowData.admins.some((admin) => admin._id === currentUserID);
        const isSCIMTeam = scimTeamsData.some((team) => team.prophecyUID === rowData._id);

        const getTooltipMessage = () => {
          let disabledMessage = '';
          if (isSCIMTeam) {
            disabledMessage = 'You are not authorized to delete this team.';
          } else if (!isAdmin) {
            disabledMessage = `Only team admin can delete the team.`;
          } else if (isPersonal) {
            disabledMessage = `Deleting personal team is not allowed.`;
          } else if (isGlobal) {
            disabledMessage = `Deleting global team is not allowed`;
          } else {
            disabledMessage = `Delete ${rowData.name || Entity.Team.toLowerCase()}`;
          }
          return disabledMessage;
        };
        return (
          <TableActions gap={theme.spaces.x6} align='end' alignY='center'>
            <>
              <Button
                size='xs'
                variant='secondary'
                onClick={() => navigate(Private_Routes.Team.home.getUrl({ uid: rowData._id }))}
                data-test-id='team-actions-manage-team-button'
                icon={<ArrowNarrowRightIcon type='default' />}>
                Manage Team
              </Button>
              <TableActionDropdown
                data-test-id='team-actions-dropdown'
                portalled={false}
                placement='bottom'
                placementOffset={2}
                align='end'
                alignOffset={-10}
                overlay={
                  <>
                    <Tooltip title={getTooltipMessage()}>
                      <Dropdown.Item
                        onSelect={() => setDeleteTeamEntity(rowData)}
                        disabled={!isAdmin || isPersonal || isGlobal || isSCIMTeam || isTeamsLoading}
                        data-test-id='team-actions-dropdown-delete-button'>
                        <Text level='sm' tone={theme.textColor.errorPrimary}>
                          Delete
                        </Text>
                      </Dropdown.Item>
                    </Tooltip>
                  </>
                }>
                <Button
                  variant='plain'
                  icon={<DotsVerticalIcon type='default' size='xs' color={theme.textColor.quaternary} />}
                  aria-label={`Open actions menu for ${rowData.name}`}
                  data-test-id='team-actions-dropdown-trigger'
                />
              </TableActionDropdown>
            </>
          </TableActions>
        );
      }
    }
  ].filter(Boolean) as ColumnType<TeamWithAspects>[];

  const dialog = deleteTeamEntity ? (
    <ProjectContextProvider>
      <CodeGenerationContextProvider>
        <DeleteConfirmationDialog
          onOk={() => deleteTeam(deleteTeamEntity._id)}
          onClose={() => setDeleteTeamEntity(undefined)}
          entity={Entity.Team}
          message={'including all the pipelines, datasets, fabrics, projects and jobs.'}
          entityName={deleteTeamEntity.name}
        />
      </CodeGenerationContextProvider>
    </ProjectContextProvider>
  ) : null;

  return { columns, dialog };
};

const TeamSortConfig = {
  sortOrder: SortOrder.desc,
  sortColumn: 'created'
};

export const TeamList = ({ teamList, isLoading }: { teamList: TeamWithAspects[]; isLoading: boolean }) => {
  const { columns, dialog } = useColumns();

  return (
    <>
      {dialog}
      <Table
        virtualize={true}
        width='100%'
        height='100%'
        columns={columns}
        dataSource={teamList}
        sortConfig={TeamSortConfig}
        loading={isLoading}
        entityName={`${Entity.Team}s`}
        data-test-id='teams-table'
      />
    </>
  );
};
